import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Plus, Save, Clapperboard, Wand2, Loader2, Music, Upload, FolderPlus,
    Download, Share2, Send, Trash2, Film, FolderOpen,
} from "lucide-react";
import { EmptyState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReelPreview } from "./ReelPreview";
import { ReelTimeline } from "./ReelTimeline";
import { reelsApi } from "@/lib/reels";
import { api, fileUrl } from "@/lib/api";
import { cn } from "@/lib/utils";
import { SocialShareDialog } from "./SocialShareDialog";

const DEFAULTS = { name: "Untitled Reel", aspect: "16:9", transition: "crossfade", music: "none", intro_title: "", folder_id: null };

export function ReelBuilder() {
    const [lib, setLib] = useState(null);
    const [segments, setSegments] = useState([]);
    const [settings, setSettings] = useState(DEFAULTS);
    const [musicFile, setMusicFile] = useState(null);
    const [projectId, setProjectId] = useState(null);
    const [durations, setDurations] = useState({});
    const [busy, setBusy] = useState("");
    const [publishing, setPublishing] = useState(null);
    const musicRef = useRef(null);

    const load = async () => setLib(await reelsApi.library());
    useEffect(() => { load(); }, []);

    const sourceClips = (lib?.clips || []).filter((c) => c.kind !== "reel");
    const reels = (lib?.clips || []).filter((c) => c.kind === "reel");
    const clipsById = Object.fromEntries((lib?.clips || []).map((c) => [c.id, c]));
    const setS = (patch) => setSettings((s) => ({ ...s, ...patch }));
    const onDuration = (id, d) => setDurations((m) => (m[id] ? m : { ...m, [id]: d }));

    const addClip = (c) => {
        const max = Math.min(8, Math.ceil(durations[c.id] || c.duration_sec || 8));
        setSegments((s) => [...s, { clip_id: c.id, in_sec: 0, out_sec: max, caption: "" }]);
        toast.success(`Added "${c.title}" to timeline`);
    };
    const updateSeg = (i, patch) => setSegments((s) => s.map((seg, j) => (j === i ? { ...seg, ...patch } : seg)));
    const moveSeg = (i, dir) => setSegments((s) => {
        const j = i + dir;
        if (j < 0 || j >= s.length) return s;
        const n = [...s]; [n[i], n[j]] = [n[j], n[i]]; return n;
    });
    const removeSeg = (i) => setSegments((s) => s.filter((_, j) => j !== i));

    const body = () => ({ ...settings, music_file: musicFile, segments });

    const saveDraft = async () => {
        setBusy("save");
        try {
            const data = projectId ? await reelsApi.updateProject(projectId, body()) : await reelsApi.saveProject(body());
            setProjectId(data.id);
            toast.success("Draft saved.");
            load();
        } catch { toast.error("Could not save draft."); }
        finally { setBusy(""); }
    };

    const exportReel = async () => {
        if (segments.length === 0) { toast.error("Add at least one clip first."); return; }
        setBusy("export");
        try {
            const proj = projectId ? await reelsApi.updateProject(projectId, body()) : await reelsApi.saveProject(body());
            setProjectId(proj.id);
            await reelsApi.render(proj.id);
            toast.success("Rendering your reel… this takes a few seconds.");
            await poll(proj.id);
        } catch (e) { toast.error(e.response?.data?.detail || "Export failed."); setBusy(""); }
    };

    const poll = async (pid) => {
        for (let i = 0; i < 40; i++) {
            await new Promise((r) => setTimeout(r, 2500));
            const p = await reelsApi.getProject(pid);
            if (p.render_status === "done") { toast.success("Reel exported to your library!"); await load(); setBusy(""); return; }
            if (p.render_status === "failed") { toast.error(`Render failed: ${p.render_error || "unknown error"}`); setBusy(""); return; }
        }
        toast.message("Still rendering — check back in your library shortly.");
        setBusy("");
    };

    const aiBuild = async () => {
        setBusy("ai");
        try {
            const spec = await reelsApi.aiBuild({ vibe: settings.music !== "none" ? settings.music : undefined, aspect: settings.aspect, max_clips: 6 });
            setSettings((s) => ({ ...s, name: spec.name, intro_title: spec.intro_title, transition: spec.transition, music: spec.music, aspect: spec.aspect }));
            setSegments(spec.segments.map((x) => ({ ...x, caption: x.caption || "" })));
            setProjectId(null);
            if (spec.generation_status === "fallback") toast.warning("AI was unavailable; clips were ordered by rating.");
            else toast.success("AI built a reel — tweak it and export!");
        } catch (e) { toast.error(e.response?.data?.detail || "AI build failed."); }
        finally { setBusy(""); }
    };

    const loadDraft = async (p) => {
        setProjectId(p.id);
        setSettings({ name: p.name, aspect: p.aspect, transition: p.transition, music: p.music, intro_title: p.intro_title || "", folder_id: p.folder_id });
        setMusicFile(p.music_file || null);
        setSegments((p.segments || []).map((x) => ({ ...x, caption: x.caption || "" })));
        toast.success(`Loaded "${p.name}"`);
    };
    const newProject = () => { setProjectId(null); setSegments([]); setSettings(DEFAULTS); setMusicFile(null); };

    const uploadMusic = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy("music");
        try { const r = await reelsApi.uploadMusic(file); setMusicFile(r.music_file); toast.success(`Music "${r.filename}" attached.`); }
        catch (err) { toast.error(err.response?.data?.detail || "Music upload failed."); }
        finally { setBusy(""); e.target.value = ""; }
    };

    const addFolder = async () => {
        const name = window.prompt("New folder name");
        if (!name) return;
        try { await reelsApi.createFolder(name); toast.success("Folder created."); load(); }
        catch { toast.error("Could not create folder."); }
    };

    const shareReel = async (id) => {
        try {
            const { data } = await api.post(`/clips/${id}/share`);
            const url = `${window.location.origin}${data.path}`;
            try { await navigator.clipboard.writeText(url); toast.success("Share link copied!"); } catch { toast.success(url); }
        } catch { toast.error("Could not create share link."); }
    };
    const deleteReel = async (id) => {
        if (!window.confirm("Delete this reel?")) return;
        try { await api.delete(`/clips/${id}`); toast("Reel deleted."); load(); } catch { toast.error("Delete failed."); }
    };

    if (!lib) return <div className="py-16 text-center text-muted-foreground font-mono text-sm">Loading reel builder…</div>;

    return (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6" data-testid="reel-builder">
            {/* LEFT — clip library + drafts */}
            <div className="xl:col-span-3 space-y-5">
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Clip Library</h3>
                        <button onClick={newProject} className="text-[11px] text-volt hover:underline flex items-center gap-1" data-testid="new-reel"><Plus className="w-3 h-3" /> New</button>
                    </div>
                    <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                        {sourceClips.length === 0 && <p className="text-xs text-muted-foreground">No video clips yet. Upload or capture clips first.</p>}
                        {sourceClips.map((c) => (
                            <button key={c.id} onClick={() => addClip(c)} data-testid={`add-clip-${c.id}`}
                                className="w-full text-left p-2.5 rounded-sm border border-border bg-surface hover:border-volt/50 transition-colors flex items-center gap-2 group">
                                <Film className="w-4 h-4 text-muted-foreground shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm text-white truncate">{c.title}</div>
                                    <div className="text-[10px] font-mono text-muted-foreground uppercase">{c.event_type}</div>
                                </div>
                                <Plus className="w-4 h-4 text-muted-foreground group-hover:text-volt shrink-0" />
                            </button>
                        ))}
                    </div>
                </div>

                {lib.projects?.length > 0 && (
                    <div>
                        <h3 className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Saved Drafts</h3>
                        <div className="space-y-1.5">
                            {lib.projects.filter((p) => p.render_status !== undefined).map((p) => (
                                <button key={p.id} onClick={() => loadDraft(p)} data-testid={`draft-${p.id}`}
                                    className={cn("w-full text-left p-2.5 rounded-sm border text-sm flex items-center gap-2 transition-colors",
                                        projectId === p.id ? "border-volt bg-volt/10 text-white" : "border-border bg-surface text-foreground/90 hover:border-white/25")}>
                                    <Clapperboard className="w-3.5 h-3.5 text-volt shrink-0" />
                                    <span className="truncate flex-1">{p.name}</span>
                                    <span className="text-[9px] font-mono uppercase text-muted-foreground">{p.render_status}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* CENTER — preview + timeline */}
            <div className="xl:col-span-6 space-y-5">
                <ReelPreview segments={segments} clipsById={clipsById} aspect={settings.aspect} onDuration={onDuration} />
                <div>
                    <h3 className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Timeline — {segments.length} clip{segments.length !== 1 ? "s" : ""}</h3>
                    <ReelTimeline segments={segments} clipsById={clipsById} durations={durations} onUpdate={updateSeg} onMove={moveSeg} onRemove={removeSeg} />
                </div>
            </div>

            {/* RIGHT — settings + actions + exports */}
            <div className="xl:col-span-3 space-y-4">
                <div className="bg-surface border border-border rounded-sm p-4 space-y-3">
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Reel Name</label>
                        <Input value={settings.name} onChange={(e) => setS({ name: e.target.value })} className="bg-obsidian border-border rounded-sm h-9 mt-1 text-sm" data-testid="reel-name" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Intro Title</label>
                        <Input value={settings.intro_title} onChange={(e) => setS({ intro_title: e.target.value })} placeholder="e.g. INSANE CLUTCH" className="bg-obsidian border-border rounded-sm h-9 mt-1 text-sm" data-testid="reel-intro" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Aspect</label>
                            <Select value={settings.aspect} onValueChange={(v) => setS({ aspect: v })}>
                                <SelectTrigger className="h-9 mt-1 bg-obsidian border-border rounded-sm text-sm" data-testid="reel-aspect"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-surface2 border-border">
                                    <SelectItem value="16:9">16:9 · YouTube</SelectItem>
                                    <SelectItem value="9:16">9:16 · Shorts</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Transition</label>
                            <Select value={settings.transition} onValueChange={(v) => setS({ transition: v })}>
                                <SelectTrigger className="h-9 mt-1 bg-obsidian border-border rounded-sm text-sm" data-testid="reel-transition"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-surface2 border-border">
                                    <SelectItem value="crossfade">Crossfade</SelectItem>
                                    <SelectItem value="cut">Hard Cut</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Music className="w-3 h-3" /> Music</label>
                        <Select value={musicFile ? "custom" : settings.music} onValueChange={(v) => { if (v !== "custom") { setMusicFile(null); setS({ music: v }); } }}>
                            <SelectTrigger className="h-9 mt-1 bg-obsidian border-border rounded-sm text-sm" data-testid="reel-music"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-surface2 border-border">
                                {(lib.music || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                                {musicFile && <SelectItem value="custom">Your track</SelectItem>}
                            </SelectContent>
                        </Select>
                        <button onClick={() => musicRef.current?.click()} className="mt-2 text-[11px] text-volt hover:underline flex items-center gap-1" data-testid="upload-music">
                            <Upload className="w-3 h-3" /> {musicFile ? "Replace your track" : "Upload your own track"}
                        </button>
                        <input ref={musicRef} type="file" accept="audio/*" className="hidden" onChange={uploadMusic} />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                            <span className="flex items-center gap-1"><FolderOpen className="w-3 h-3" /> Folder</span>
                            <button onClick={addFolder} className="text-volt hover:underline flex items-center gap-0.5" data-testid="add-folder"><FolderPlus className="w-3 h-3" /> New</button>
                        </label>
                        <Select value={settings.folder_id || "none"} onValueChange={(v) => setS({ folder_id: v === "none" ? null : v })}>
                            <SelectTrigger className="h-9 mt-1 bg-obsidian border-border rounded-sm text-sm" data-testid="reel-folder"><SelectValue placeholder="No folder" /></SelectTrigger>
                            <SelectContent className="bg-surface2 border-border">
                                <SelectItem value="none">No folder</SelectItem>
                                {(lib.folders || []).map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="space-y-2">
                    <button onClick={aiBuild} disabled={!!busy} data-testid="ai-build"
                        className="w-full h-10 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
                        {busy === "ai" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} AI Auto-Reel
                    </button>
                    <div className="grid grid-cols-2 gap-2">
                        <button onClick={saveDraft} disabled={!!busy} data-testid="save-reel"
                            className="h-10 rounded-sm border border-border text-white hover:border-white/40 font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
                            {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                        </button>
                        <button onClick={exportReel} disabled={!!busy || segments.length === 0} data-testid="export-reel"
                            className="h-10 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
                            {busy === "export" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4" />} Export
                        </button>
                    </div>
                </div>

                <div>
                    <h3 className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Exported Reels</h3>
                    {reels.length === 0
                        ? <p className="text-xs text-muted-foreground">No exported reels yet. Build one and hit Export.</p>
                        : <div className="space-y-2">
                            {reels.map((r) => (
                                <div key={r.id} data-testid={`reel-export-${r.id}`} className="bg-surface border border-border rounded-sm p-2.5">
                                    <div className="text-sm text-white truncate mb-2">{r.title}</div>
                                    <div className="flex items-center gap-1.5">
                                        <a href={fileUrl(r.video_media_url)} download className="flex-1 h-8 rounded-sm bg-volt/10 text-volt text-xs font-semibold flex items-center justify-center gap-1 hover:bg-volt/20 transition-colors" data-testid={`download-reel-${r.id}`}><Download className="w-3.5 h-3.5" /> Download</a>
                                        <button onClick={() => shareReel(r.id)} className="h-8 w-8 grid place-items-center rounded-sm border border-border text-muted-foreground hover:text-volt" title="Share" data-testid={`share-reel-${r.id}`}><Share2 className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => setPublishing(r)} title="Publish or share" className="h-8 w-8 grid place-items-center rounded-sm border border-volt/40 text-volt hover:bg-volt/10" data-testid={`publish-reel-${r.id}`}><Send className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => deleteReel(r.id)} className="h-8 w-8 grid place-items-center rounded-sm border border-border text-muted-foreground hover:text-alert" title="Delete" data-testid={`delete-reel-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                            ))}
                        </div>}
                </div>
            </div>

            <SocialShareDialog asset={publishing} open={!!publishing} onOpenChange={(open) => !open && setPublishing(null)} />
        </div>
    );
}
